<script setup lang="ts">
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';
import Textarea from 'primevue/textarea';
import Button from 'primevue/button';
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';
import TabPanel from 'primevue/tabpanel';
import { computed, ref, watch } from 'vue';
import { InputNumber, InputGroup, InputGroupAddon, ToggleSwitch, Fieldset } from 'primevue';
import Message from 'primevue/message';
import ProblemDetailsView from 'shared/problems/components/ProblemDetailsView.vue';
import type { ProblemDetails } from "shared/problems/domain/problemDetails";
import { useCreateVehicle } from '../api/createVehicle';
import { useUpdateVehicle } from '../api/updateVehicle';
import { VehicleType } from 'shared/vehicles/domain/vehicleType';
import { Assert } from 'shared/assert';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { storeToRefs } from 'pinia';
import DescriptionIcon from 'shared/common/components/DescriptionIcon.vue';
import { useVehicleStore } from 'shared/vehicles/stores/vehicleStore';
import type { VehicleRequest } from '../api/models/vehicleRequest';
import type { Vehicle } from 'shared/vehicles/domain/vehicle';
import ColorPickerField from 'shared/forms/components/ColorPickerField.vue';
import { useKeybindStore } from 'shared/vehicles/stores/keybindStore';
import KeyboardActionsForm from './KeyboardActionsForm.vue';
import VehicleSymbolPreview from './VehicleSymbolPreview.vue';
import { VehicleSymbol } from 'shared/vehicles/domain/vehicleSymbol';

defineExpose({
  show
});

const createVehicle = useCreateVehicle();
const updateVehicle = useUpdateVehicle();
const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);
const vehicleStore = useVehicleStore();
const keybindStore = useKeybindStore();
const { isFormChanged } = storeToRefs(keybindStore);

const loading = ref(false);
const problems = ref<ProblemDetails>();

const types = [
  VehicleType.Undefined,
  VehicleType.Automatic,
  VehicleType.LogHandler,
  VehicleType.Truck,
  VehicleType.CoilForklift,
  VehicleType.BulkHandler,
];

const typeOptions = types.map<TypeOption>((t) => {
  return {
    value: t,
    name: translations.value.vehicleType(t)
  };
});

const defaultType = typeOptions.find(o => o.value === VehicleType.LogHandler) ?? typeOptions[0]!;

const symbols = [
  VehicleSymbol.Undefined,
  VehicleSymbol.LogStacker,
  VehicleSymbol.WheelLoaderFrontGrapple,
  VehicleSymbol.WheelLoaderHighGrapple,
  VehicleSymbol.TimberCrane,
  VehicleSymbol.CoilForklift,
];

const symbolOptions = symbols.map<SymbolOption>((s) => {
  return {
    value: s,
    name: translations.value.vehicleSymbol(s)
  };
});

const defaultSymbol = symbolOptions.find(o => o.value === VehicleSymbol.Undefined) ?? symbolOptions[0]!;

// Filter available symbols based on selected vehicle type
const availableSymbolOptions = computed(() => {
  const type = selectedType.value.value;

  switch (type) {
    case VehicleType.LogHandler:
      return symbolOptions.filter(s =>
        s.value === VehicleSymbol.Undefined ||
        s.value === VehicleSymbol.LogStacker ||
        s.value === VehicleSymbol.WheelLoaderFrontGrapple ||
        s.value === VehicleSymbol.WheelLoaderHighGrapple ||
        s.value === VehicleSymbol.TimberCrane
      );
    case VehicleType.CoilForklift:
      return symbolOptions.filter(s =>
        s.value === VehicleSymbol.Undefined ||
        s.value === VehicleSymbol.CoilForklift
      );
    case VehicleType.BulkHandler:
            return symbolOptions.filter(s =>
        s.value === VehicleSymbol.Undefined ||
        s.value === VehicleSymbol.WheelLoaderFrontGrapple ||
        s.value === VehicleSymbol.WheelLoaderHighGrapple
      );
    case VehicleType.Automatic:
    case VehicleType.Truck:
    case VehicleType.Undefined:
    default:
      return symbolOptions.filter(s => s.value === VehicleSymbol.Undefined);
  }
});

const operation = ref<'create' | 'edit' | 'none'>('none');
const visible = ref<boolean>();
const current = ref<Vehicle>();

const title = ref('');

const name = ref('');
const nameChanged = computed(() => {
  const old = current.value;
  if (operation.value !== 'edit' || old === undefined) return false;
  return name.value !== old.name;
});
const nameError = computed(() => {

  if (name.value === '') {
    return translations.value.error.nameIsRequired;
  }

  if (name.value.length > 100) {
    return translations.value.error.maxLength(100);
  }

  return undefined;

});

const color = ref('3b82f6');
const colorChanged = computed(() => {
  const old = current.value;
  if (operation.value !== 'edit' || old === undefined) return false;
  return color.value !== old.color;
});

const description = ref('');
const descriptionChanged = computed(() => {
  const old = current.value;
  if (operation.value !== 'edit' || old === undefined) return false;
  return description.value !== old.description;
});

const selectedType = ref<TypeOption>(defaultType);
const selectedTypeChanged = computed(() => {
  const old = current.value;
  if (operation.value !== 'edit' || old === undefined) return false;
  return selectedType.value.value !== old.type;
});

const selectedSymbol = ref<SymbolOption>(defaultSymbol);
const selectedSymbolChanged = computed(() => {
  const old = current.value;
  if (operation.value !== 'edit' || old === undefined) return false;
  return selectedSymbol.value.value !== old.symbol;
});

// Watch for type changes and reset symbol if it's no longer available
watch(selectedType, () => {
  const isSymbolAvailable = availableSymbolOptions.value.some(
    option => option.value === selectedSymbol.value.value
  );

  if (!isSymbolAvailable) {
    selectedSymbol.value = availableSymbolOptions.value[0] ?? defaultSymbol;
  }
});

const capacityEnabled = computed(() => {
  return selectedType.value.value === VehicleType.LogHandler || VehicleType.BulkHandler;
});

const capacity = ref<number>();

const capacityChanged = computed(() => {
  if (!capacityEnabled.value) return false;
  const old = current.value;
  if (operation.value !== 'edit' || old === undefined) return false;
  return capacity.value !== old.capacity;
});

const capacityError = computed(() => {

  if (!capacityEnabled.value) return undefined;

  if (capacity.value === undefined || capacity.value === null) {
    return translations.value.error.required;
  }

  return undefined;
});

const automaticLogYardMoveVisible = computed(() => selectedType.value.value === VehicleType.Automatic);

const automaticLogYardMoveEnabled = ref(false);

const automaticLogYardMoveEnabledChanged = computed(() => {
  if (!automaticLogYardMoveVisible.value) return false;
  const old = current.value;
  if (operation.value !== 'edit' || old === undefined) return false;
  return automaticLogYardMoveEnabled.value !== old.automaticLogYardMoves.enabled;
});

const automaticLogYardMoveLogSorterBinFull = ref(false);

const automaticLogYardMoveLogSorterBinFullChanged = computed(() => {
  if (!automaticLogYardMoveVisible.value) return false;
  const old = current.value;
  if (operation.value !== 'edit' || old === undefined) return false;
  return automaticLogYardMoveLogSorterBinFull.value !== old.automaticLogYardMoves.logSorterBinFull;
});

const automaticLogYardMoveLogSorterBinChangeProduct = ref(false);

const automaticLogYardMoveLogSorterBinChangeProductChanged = computed(() => {
  if (!automaticLogYardMoveVisible.value) return false;
  const old = current.value;
  if (operation.value !== 'edit' || old === undefined) return false;
  return automaticLogYardMoveLogSorterBinChangeProduct.value !== old.automaticLogYardMoves.logSorterBinChangeProduct;
});

const valid = computed(() => {
  return nameError.value === undefined &&
    capacityError.value === undefined;
});

const hasChanges = computed(() => {
  return nameChanged.value ||
    descriptionChanged.value ||
    selectedTypeChanged.value ||
    selectedSymbolChanged.value ||
    capacityChanged.value ||
    colorChanged.value ||
    automaticLogYardMoveEnabledChanged.value ||
    automaticLogYardMoveLogSorterBinFullChanged.value ||
    automaticLogYardMoveLogSorterBinChangeProductChanged.value ||
    isFormChanged.value;
});

const saveEnabled = computed(() => {
  if (!valid.value) return false;
  if (operation.value === 'create') return true;
  if (operation.value === 'edit' && hasChanges.value) return true;
  return false;
})

interface TypeOption {
  name: string;
  value: VehicleType;
}

interface SymbolOption {
  name: string;
  value: VehicleSymbol;
}

interface ShowDialog {
  vehicleId?: string;
}

async function show(options: ShowDialog) {
  try {
    loading.value = true;
    problems.value = undefined;

    const id = options.vehicleId;

    if (id === undefined) {
      showCreate();
    } else {
      await showEdit(id);
    }

  } finally {
    loading.value = false;
  }
}

function showCreate() {
  current.value = undefined;

  title.value = translations.value.vehicles.addNewVehicle;
  name.value = '';
  description.value = '';
  capacity.value = undefined;
  color.value = '3b82f6';
  selectedType.value = defaultType;
  selectedSymbol.value = availableSymbolOptions.value[0] ?? defaultSymbol;
  automaticLogYardMoveEnabled.value = false;
  automaticLogYardMoveLogSorterBinChangeProduct.value = false;
  automaticLogYardMoveLogSorterBinFull.value = false;

  keybindStore.parseKeybindings('', false, 'default');

  operation.value = 'create';
  visible.value = true;
}

async function showEdit(id: string) {
  const keybindStore = useKeybindStore();

  title.value = translations.value.vehicles.editVehicle;

  const vehicle = vehicleStore.getById(id);
  Assert.definied(vehicle);

  current.value = vehicle;
  name.value = vehicle.name;
  color.value = vehicle.color ?? '3b82f6';
  description.value = vehicle.description;
  selectedType.value = typeOptions.find(o => o.value === vehicle.type) ?? defaultType;

  // Set symbol only if it's available for the selected type, otherwise use first available
  const vehicleSymbolOption = symbolOptions.find(o => o.value === vehicle.symbol);
  const isSymbolAvailableForType = vehicleSymbolOption &&
    availableSymbolOptions.value.some(option => option.value === vehicleSymbolOption.value);
  selectedSymbol.value = isSymbolAvailableForType
    ? vehicleSymbolOption
    : (availableSymbolOptions.value[0] ?? defaultSymbol);

  capacity.value = vehicle.capacity ?? undefined;
  automaticLogYardMoveEnabled.value = vehicle.automaticLogYardMoves.enabled;
  automaticLogYardMoveLogSorterBinChangeProduct.value = vehicle.automaticLogYardMoves.logSorterBinChangeProduct;
  automaticLogYardMoveLogSorterBinFull.value = vehicle.automaticLogYardMoves.logSorterBinFull;

  keybindStore.parseKeybindings(vehicle.keybinds, vehicle.lockKeybinds, 'edit');

  operation.value = 'edit';
  visible.value = true;
}

async function onSubmit() {
  if (!valid.value) return;

  try {
    loading.value = true;

    if (operation.value === 'create') {
      await create();
    } else {
      const id = current.value?.id;
      Assert.definied(id);
      await update(id);
    }

  } finally {
    loading.value = false;
  }
}

async function create() {
  const request = createRequest(crypto.randomUUID());
  const response = await createVehicle.execute(request);

  if (response.ok) {
    operation.value = 'none';
    visible.value = false;
  } else {
    problems.value = response.details;
  }
}

async function update(id: string) {

  const request = createRequest(id);
  const response = await updateVehicle.execute(request);

  if (response.ok) {
    operation.value = 'none';
    visible.value = false;
  } else {
    problems.value = response.details;
  }
}

function createRequest(id: string) {
  const request: VehicleRequest = {
    id: id,
    name: name.value,
    description: description.value,
    type: selectedType.value.value,
    color: color.value,
    capacity: capacity.value ?? null,
    keybinds: keybindStore.stringifyFormData(),
    lockKeybinds: keybindStore.isLocked,
    automaticLogYardMoves: {
      enabled: automaticLogYardMoveVisible.value && automaticLogYardMoveEnabled.value,
      logSorterBinChangeProduct: automaticLogYardMoveVisible.value && automaticLogYardMoveLogSorterBinChangeProduct.value,
      logSorterBinFull: automaticLogYardMoveVisible.value && automaticLogYardMoveLogSorterBinFull.value
    },
    symbol: selectedSymbol.value.value,
  };

  return request;
}

function onHide() {
  current.value = undefined;
  operation.value = 'none';
}

const capacityUnit = computed(() => {
  return selectedType.value.value == VehicleType.BulkHandler ? "t" : "m²"
});

</script>

<template>
  <Dialog modal v-model:visible="visible" :header="title" @hide="onHide" class="w-lg">
    <div class="edit-dialog">
      <ProblemDetailsView class="problem-details" v-model="problems"></ProblemDetailsView>
      <Tabs value="0">
        <TabList>
          <Tab value="0">{{ translations.generic.settings }}</Tab>
          <Tab value="1">{{ translations.generic.keybindings }}</Tab>
        </TabList>
        <TabPanel value="0">
          <div class="vehicle-form">
            <div class="field" :class="{ changed: nameChanged }">
              <label for="edit-vehicle-dialog-name">{{ translations.generic.name }}</label>
              <InputText autocomplete="off" v-model:modelValue="name" autofocus id="edit-vehicle-dialog-name"
                name="name">
              </InputText>
              <Message v-if="nameError" severity="error" variant="simple">{{ nameError }}</Message>
            </div>
            <div class="field" :class="{ changed: selectedTypeChanged }">
              <label for="edit-vehicle-dialog-type">{{ translations.generic.type }}</label>
              <Select autocomplete="off" id="edit-vehicle-dialog-type" name="type" :options="typeOptions"
                optionLabel="name" v-model="selectedType"></Select>
            </div>
            <div class="field" :class="{ changed: selectedSymbolChanged }">
              <label for="edit-vehicle-dialog-symbol">{{ translations.vehicles.symbol }}</label>
              <Select autocomplete="off" id="edit-vehicle-dialog-symbol" name="symbol" :options="availableSymbolOptions"
                optionLabel="name" v-model="selectedSymbol"></Select>
            </div>
            <ColorPickerField :changed="colorChanged" v-model:model-value="color"></ColorPickerField>
            <div class="preview-field">
              <label>{{ translations.vehicles.symbolPreview }}</label>
              <VehicleSymbolPreview :symbol="selectedSymbol.value" :color="color" :size="100"></VehicleSymbolPreview>
            </div>
            <div class="field" :class="{ changed: capacityChanged }" v-if="capacityEnabled">
              <div class="field-header">
                <label for="edit-vehicle-dialog-capacity">{{ translations.generic.capacity }}</label>
                <DescriptionIcon :description="translations.vehicles.capacityDescription"></DescriptionIcon>
              </div>
              <InputGroup>
                <InputNumber autocomplete="off" :maxFractionDigits="2" input-id="edit-vehicle-dialog-capacity"
                  v-model:model-value="capacity" name="capacity"></InputNumber>
                <InputGroupAddon>{{capacityUnit}}</InputGroupAddon>
              </InputGroup>
              <Message v-if="capacityError" severity="error" variant="simple">{{ capacityError }}</Message>
            </div>
            <div class="field description-field" :class="{ changed: descriptionChanged }">
              <label for="edit-vehicle-dialog-description">{{ translations.generic.description }}</label>
              <Textarea autocomplete="off" v-model:modelValue="description" id="edit-vehicle-dialog-description"
                name="description"></Textarea>
            </div>
            <Fieldset class="automatic-log-yard-moves" :legend="translations.vehicles.automaticLogYardMoves"
              v-if="automaticLogYardMoveVisible">
              <div class="toggle-field" :class="{ changed: automaticLogYardMoveEnabledChanged }">
                <ToggleSwitch v-model:modelValue="automaticLogYardMoveEnabled"
                  id="edit-vehicle-dialog-automatic-log-yard-moves-enabled">
                </ToggleSwitch>
                <label for="edit-vehicle-dialog-automatic-log-yard-moves-enabled">
                  {{ translations.generic.enabled }}
                </label>
              </div>
              <div class="toggle-field" :class="{ changed: automaticLogYardMoveLogSorterBinFullChanged }">
                <ToggleSwitch v-model:modelValue="automaticLogYardMoveLogSorterBinFull"
                  id="edit-vehicle-dialog-automatic-log-yard-moves-log-sorter-bin-full">
                </ToggleSwitch>
                <label for="edit-vehicle-dialog-automatic-log-yard-moves-log-sorter-bin-full">
                  {{ translations.vehicles.automaticLogYardMovesLogSorterBinFull }}
                </label>
              </div>
              <div class="toggle-field" :class="{ changed: automaticLogYardMoveLogSorterBinChangeProductChanged }">
                <ToggleSwitch v-model:modelValue="automaticLogYardMoveLogSorterBinChangeProduct"
                  id="edit-vehicle-dialog-automatic-log-yard-moves-log-sorter-change-product">
                </ToggleSwitch>
                <label for="edit-vehicle-dialog-automatic-log-yard-moves-log-sorter-change-product">
                  {{ translations.vehicles.automaticLogYardMovesLogSorterBinChangeProduct }}
                </label>
              </div>
            </Fieldset>
          </div>
        </TabPanel>
        <TabPanel value="1">
          <KeyboardActionsForm></KeyboardActionsForm>
        </TabPanel>
      </Tabs>
    </div>
    <template #footer>
      <Button class="submit-button" @click="onSubmit" :label="translations.generic.save" type="submit"
        :disabled="!saveEnabled" :loading="loading"></Button>
    </template>
  </Dialog>
</template>

<style scoped>
.edit-dialog {
  max-height: 80vh;
  overflow: hidden;
}

.problem-details {
  margin-top: 1px;
  margin-bottom: 0.5rem;
}

.vehicle-form {
  display: grid;
  row-gap: .5rem;
  column-gap: .5rem;
  grid-template-columns: repeat(2, 1fr);
  margin-top: 1rem;
}

.keyboard-shortcuts-form {
  display: grid;
  row-gap: .5rem;
  column-gap: .5rem;
  grid-template-columns: repeat(3, 1fr);
  margin-top: 1rem;
}

.description-field {
  grid-column: 1/3;
}

.preview-field {
  display: flex;
  flex-direction: column;
}

.color-field :deep(.p-inputgroupaddon) {
  padding-block: 6px;
}

.field {
  display: flex;
  flex-direction: column;
}

.submit-button {
  justify-self: end;
  grid-column: 1/3;
}

.container {
  display: flex;
  flex-direction: column;
  flex: 2;
}

.field :deep(.p-inputnumber-input) {
  text-align: right;
}

.toggle-field {
  display: flex;
  align-items: center;
  column-gap: .5rem;
}

.automatic-log-yard-moves {
  grid-column: 1/3;
}

.automatic-log-yard-moves :deep(.p-fieldset-content) {
  row-gap: .5rem;
  display: flex;
  flex-direction: column;
}
</style>
